import React, { memo } from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Stack,
	Divider,
	useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";

const DataCard = ({
	data = [],
	columns = [],
	onRowClick = null,
	rowKey = "id",
	emptyStateIcon: EmptyIcon = null,
	emptyStateTitle = "No data available",
	emptyStateSubtitle = "",
	containerProps = {},
	rowProps = {},
	cellProps = {},
	rowOverlay = null,
	// ignoring table-specific props like stickyHeader, sortConfig, maxHeight
}) => {
	const [t] = useTranslation();
	const theme = useTheme();
	const isRTL = theme.direction === 'rtl';

	// Helper function to get unique key for each row
	const getRowKey = (item, index) => {
		if (rowKey && item[rowKey] != null) {
			return item[rowKey];
		}
		return `row-${index}`;
	};

	const renderCellContent = (item, column, rowIndex) => {
		if (column.render) {
			return column.render(item, rowIndex);
		}
		if (column.key) {
			return item[column.key] || "";
		}
		return "";
	};

	// ✅ Empty state
	if (!data || data.length === 0) {
		return (
			<Box sx={{ textAlign: "center", py: 8 }}>
				{EmptyIcon && (
					<EmptyIcon
						sx={{
							fontSize: 64,
							color: "text.secondary",
							mb: 2
						}}
					/>
				)}
				<Typography variant="h6" color="text.secondary" gutterBottom>
					{emptyStateTitle}
				</Typography>
				{emptyStateSubtitle && (
					<Typography variant="body2" color="text.secondary">
						{emptyStateSubtitle}
					</Typography>
				)}
			</Box>
		);
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...containerProps }}>
			{data.map((item, rowIndex) => (
				<Card
					key={getRowKey(item, rowIndex)}
					onClick={onRowClick ? () => onRowClick(item, rowIndex) : undefined}
					sx={{
						position: 'relative',
						overflow: 'visible',
						...(onRowClick && { 
							cursor: "pointer", 
							transition: "transform 0.2s, box-shadow 0.2s", 
							'&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] } 
						}),
						...rowProps,
						...(typeof rowProps === "function" ? rowProps(item, rowIndex) : {}),
					}}
				>
					<CardContent sx={{ pb: "16px !important", p: 2 }}>
						<Stack spacing={1.5} divider={<Divider flexItem />}>
							{columns.map((column, colIndex) => (
								<Box 
									key={column.key || colIndex}
									sx={{ 
										display: 'flex', 
										justifyContent: 'space-between',
										alignItems: 'center', // use center to keep short text aligned elegantly
										gap: 2,
										...cellProps,
										...column.cellStyle,
									}}
								>
									{/* Label */}
									{column.label && (
										<Typography 
											variant="body2" 
											color="text.secondary" 
											sx={{ 
												fontWeight: 600, 
												minWidth: '35%', 
												textAlign: isRTL ? 'right' : 'left'
											}}
										>
											{t(column.label)}
										</Typography>
									)}
									
									{/* Value/Content */}
									<Box 
										sx={{ 
											textAlign: column.label ? (isRTL ? 'left' : 'right') : 'left', 
											flexGrow: 1, 
											wordBreak: 'break-word',
											display: 'flex',
											flexDirection: 'column',
											alignItems: column.label ? (isRTL ? 'flex-start' : 'flex-end') : 'stretch',
											justifyContent: 'center',
											minHeight: '28px' // Ensures consistent height even for empty values
										}}
									>
										{renderCellContent(item, column, rowIndex)}
									</Box>
								</Box>
							))}
						</Stack>
						
						{/* Render overlay if present (often used for actions) */}
						{rowOverlay && (
							<Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'flex-end' }}>
								{rowOverlay(item, rowIndex)}
							</Box>
						)}
					</CardContent>
				</Card>
			))}
		</Box>
	);
};

export default memo(DataCard);
